import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Location, Screen } from '@ghostscreen/contracts';
import { FilterChips } from '../../components/dashboard/FilterChips';
import { getCardConnectionStatus } from '../../lib/dashboard/screenStatus';
import { DisplayGroup } from './DisplayGroup';

const FILTERS = ['All', 'Online', 'Offline', 'Unknown', 'Unpaired'] as const;
type Filter = (typeof FILTERS)[number];

const FILTER_LABEL_KEY = {
  All: 'board.filters.all',
  Online: 'board.filters.online',
  Offline: 'board.filters.offline',
  Unknown: 'board.filters.unknown',
  Unpaired: 'board.filters.unpaired',
} as const satisfies Record<Filter, string>;

type DisplaysBoardProps = {
  screens: Screen[];
  locations: Location[];
  onManageScreen: (screenId: string) => void;
  onPlayContent: (screenId: string) => void;
  onAddDisplay: (locationId: string) => void;
  onEditLocation: (location: Location) => void;
  onDuplicateLocation: (location: Location) => void;
  onDeleteLocation: (location: Location) => void;
  duplicatingLocationId?: string | null;
};

export function DisplaysBoard({
  screens,
  locations,
  onManageScreen,
  onPlayContent,
  onAddDisplay,
  onEditLocation,
  onDuplicateLocation,
  onDeleteLocation,
  duplicatingLocationId = null,
}: DisplaysBoardProps) {
  const { t } = useTranslation('displays');
  const [filter, setFilter] = useState<Filter>('All');
  const [query, setQuery] = useState('');

  const locationById = useMemo(
    () => new Map(locations.map((location) => [location.id, location])),
    [locations],
  );

  const filteredScreens = useMemo(() => {
    const q = query.trim().toLowerCase();
    return screens.filter((screen) => {
      const status = getCardConnectionStatus(screen);
      if (filter === 'Online' && status !== 'online') return false;
      if (filter === 'Offline' && status !== 'offline') return false;
      if (filter === 'Unknown' && status !== 'unknown') return false;
      if (filter === 'Unpaired' && status !== 'unpaired') return false;
      if (!q) return true;
      const locationName = locationById.get(screen.locationId)?.name ?? '';
      return (
        screen.name.toLowerCase().includes(q) || locationName.toLowerCase().includes(q)
      );
    });
  }, [screens, filter, query, locationById]);

  const groups = useMemo(() => {
    const byLocation = new Map<string, Screen[]>();

    for (const screen of filteredScreens) {
      const list = byLocation.get(screen.locationId) ?? [];
      list.push(screen);
      byLocation.set(screen.locationId, list);
    }

    // Always keep empty locations visible under All so sites show up before any display exists.
    if (filter === 'All') {
      const q = query.trim().toLowerCase();
      for (const location of locations) {
        if (byLocation.has(location.id)) continue;
        if (q && !location.name.toLowerCase().includes(q)) continue;
        byLocation.set(location.id, []);
      }
    }

    return [...byLocation.entries()]
      .map(([locationId, groupScreens]) => {
        const location = locationById.get(locationId);
        if (!location) return null;
        return {
          location,
          screens: groupScreens,
          onlineCount: groupScreens.filter((s) => getCardConnectionStatus(s) === 'online').length,
        };
      })
      .filter((group): group is NonNullable<typeof group> => group !== null)
      .sort((a, b) => a.location.name.localeCompare(b.location.name));
  }, [filteredScreens, locations, filter, query, locationById]);

  const showStatusFilters = screens.length > 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('board.searchPlaceholder')}
          className="w-full rounded-[10px] border border-line bg-field px-3.5 py-2.5 text-sm text-ink outline-none placeholder:text-muted focus:border-signal/50 sm:max-w-xs"
        />
        {showStatusFilters ? (
          <FilterChips
            options={FILTERS}
            value={filter}
            onChange={setFilter}
            getLabel={(option) => t(FILTER_LABEL_KEY[option])}
          />
        ) : null}
      </div>

      {groups.length === 0 ? (
        <p className="text-sm text-muted">{t('board.noMatches')}</p>
      ) : (
        groups.map((group) => (
          <DisplayGroup
            key={group.location.id}
            location={group.location}
            screens={group.screens}
            onlineCount={group.onlineCount}
            onManageScreen={onManageScreen}
            onPlayContent={onPlayContent}
            onAddDisplay={onAddDisplay}
            onEditLocation={onEditLocation}
            onDuplicateLocation={onDuplicateLocation}
            onDeleteLocation={onDeleteLocation}
            duplicatingLocationId={duplicatingLocationId}
          />
        ))
      )}
    </div>
  );
}
